import { useState } from "react";

import { BodySmall, Box, Modal, ModalFooter, ModalHeader, Select, type SelectChangeEvent } from "@parspec/pixel";

import { BUSINESS_TYPE_OPTIONS, RESET_SELECT_OPTION, VISIBILITY_RADIO_OPTIONS, VISIBILITY_RADIO_VALUE } from "../constant";
import SelectOutsideSales from "../SelectOutsideSales";
import { BulkEditCompanyUpdatedData, LocationVisibilityState } from "../types";
import LocationVisibility from "./LocationVisibility";
import { LOCATION_REQUIRED_MESSAGE } from "src/features/shared/constants";
import { useBulkEditCompaniesMutation } from "../../Company/queries";
import { BulkEditCompanyRequest } from "../../Company/queries/apiTypes";

interface BulkEditCompanyProps {
    selectedCompanyIds: Array<number>;
    onClose: () => void;
    onSuccess: (message: string, updatedData: BulkEditCompanyUpdatedData) => void;
};

interface BulkEditState {
    businessType: string;
    outsideSalesAgent: number | string;
};

const sxStyle = {
    subtitle: { lineHeight: '1.25rem', paddingBottom: '8px', },
};

const FIELD_NAME = {
    OUTSIDE_SALES_AGENT: "outsideSalesAgent",
    BUSINESS_TYPE: "businessType",
} as const;

const UPDATED_BUSINESS_TYPE_OPTIONS = [RESET_SELECT_OPTION, ...BUSINESS_TYPE_OPTIONS];

export default function BulkEditCompanyModal(props: BulkEditCompanyProps) {
    const { onClose, onSuccess, selectedCompanyIds } = props;
    const { mutateAsync: bulkEdit, isLoading: isBulkEditing } = useBulkEditCompaniesMutation();

    const [bulkFields, setBulkFields] = useState<BulkEditState>({
        businessType: '',
        outsideSalesAgent: "",
    });
    const [locationVisiblity, setLocationVisibility] = useState<LocationVisibilityState>({
        visibility: VISIBILITY_RADIO_OPTIONS[0].value,
        checkedLocationsMap: new Map(),
    });

    const [visibilityErr, setVisibilityErr] = useState("");

    const { businessType, outsideSalesAgent } = bulkFields;

    const onConfirmBulkEdit = async () => {
        if (isBulkEditing) return;
        const { visibility, checkedLocationsMap } = locationVisiblity;
        const isVisibleToAll = visibility === VISIBILITY_RADIO_VALUE.ALL_BRANCH_LOCATIONS;

        if (!isVisibleToAll && !checkedLocationsMap.size) {
            // if specific branch locations radio selected but no options. throw error
            setVisibilityErr(LOCATION_REQUIRED_MESSAGE);
            return;
        }

        try {
            // setting undefined in payload will not send field to the API
            const payload: BulkEditCompanyRequest = {
                company_ids: selectedCompanyIds,
                fields: {
                    visible_to_all: isVisibleToAll,
                    visible_to: isVisibleToAll ? [] : [...checkedLocationsMap.keys()],
                    business_type: businessType === RESET_SELECT_OPTION.value
                        ? "" // explicitly send "" to backend to reset the business_type
                        : businessType || undefined,
                    outside_sales_agent: outsideSalesAgent === RESET_SELECT_OPTION.value
                        ? null // explicitly send null to backend to reset the outside_sales_agent
                        : outsideSalesAgent !== ""
                            ? Number(outsideSalesAgent)
                            : undefined,
                }
            };

            const response = await bulkEdit(payload);
            onSuccess(response.data.message, {
                ...payload,
                fields: {
                    ...payload.fields,
                    visible_to: isVisibleToAll ? [] : [...checkedLocationsMap.values()]
                },
            });
        } catch (err) { }
    };

    const handleFieldChange = (e: SelectChangeEvent<string | number>) => {
        const { value, name } = e.target;
        let newValue = value;

        setBulkFields(prev => ({ ...prev, [name]: newValue }));
    };

    const header = <ModalHeader title="Bulk Edit - Company" onClose={onClose} />
    const footer = (
        <ModalFooter
            onReject={onClose}
            onAccept={onConfirmBulkEdit}
            continueButtonLabel="Confirm"
            cancelButtonLabel="Cancel"
            isLoading={isBulkEditing}
        />
    );

    return (
        <Modal header={header} footer={footer} open borderRadius="6px">
            <Box width={500} flexDirection="column" display="flex" gap="16px">
                <Box>
                    <BodySmall color="secondary.main" sx={sxStyle.subtitle}>
                        Details
                    </BodySmall>
                    <Select
                        options={UPDATED_BUSINESS_TYPE_OPTIONS}
                        label="Business Type"
                        labelId="bulk-edit-company-business-type"
                        name={FIELD_NAME.BUSINESS_TYPE}
                        value={businessType}
                        onChange={handleFieldChange}
                    />
                </Box>

                <SelectOutsideSales
                    showResetOption
                    name={FIELD_NAME.OUTSIDE_SALES_AGENT}
                    value={outsideSalesAgent}
                    onChange={handleFieldChange}
                />

                <LocationVisibility
                    locationVisiblity={locationVisiblity}
                    setLocationVisibility={setLocationVisibility}
                    visibilityErr={visibilityErr}
                    setVisibilityErr={setVisibilityErr}
                />
            </Box>
        </Modal>
    );
}